import React from "react";
import { observer } from "mobx-react-lite";
import { styled } from "@shoothill/core";
import { FormControlLabel, RadioGroup, Checkbox, FormControl, FormGroup, FormHelperText, FormLabel, Typography } from "@material-ui/core";
import { CustomRadio } from "Views/Project/CustomComponents";
import { PrimaryButton } from "Components/Buttons/Buttons";
import { InductionAdminEditViewModel } from "./InductionAdminEditViewModel";
import moment from "moment";
import {
    ItemDetailsContainer,
    ItemDetailsSection,
    ItemDetailsSectionRow,
    AnswerTileContainer,
    AnswerTile,
    VideoResponseTileContainer,
    VideoResponseTile,
    QualificationTable,
} from "../InductionAdminDetailsView.styles";
import { DarwinDateSelect } from "Components/DateSelect/DarwinDateSelect";
import { DashedDivider } from "Views/PurchaseOrder/Form/Views/Dividers";
import { DarwinInputWrapper } from "Components/Form/DarwinInputWrapper";
import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";

interface IProps {
    className?: string;
    viewModel: InductionAdminEditViewModel;
}

const Step4Container = styled.div`
    .root {
        margin: 15px;
    }

    .form-controls {
        display: flex;
        flex-direction: column;
        margin: 16px 0;
    }

    .form-label {
        font-size: 14px !important;
        font-weight: 600;
        letter-spacing: -0.02em;
        line-height: 18px;
        text-align: left;
    }

    .form-helpertext {
        font-size: 12px;
        line-height: 1.33;
    }

    .form-group {
        flex-direction: row;
        margin-bottom: 16px;
    }
`;

const InductionAdminEditViewBase: React.FC<IProps> = observer(({ viewModel }) => {
    const getPassedValue = (val: boolean | null) => {
        if (val === null) {
            return <span>N/A</span>;
        } else if (val === true) {
            return <span className="red">Y</span>;
        } else {
            return <span>N</span>;
        }
    };

    return (
        <ItemDetailsContainer>
            {/* Personal Details Section */}
            {/* <ItemDetailsSection>
                <ItemDetailsSectionRow>
                    <div>
                        <span>First Name:</span>
                        <DarwinInput viewModel={viewModel} fieldName="firstName" validateOnBlur={true} />
                    </div>
                    <div>
                        <span>Last Name:</span>
                        <DarwinInput viewModel={viewModel} fieldName="lastName" validateOnBlur={true} />
                    </div>
                </ItemDetailsSectionRow>
            </ItemDetailsSection> */}
            {viewModel.model.isWorker && (
                <ItemDetailsSection>
                    <ItemDetailsSectionRow>
                        <div>
                            <span>Priority Employer name:</span>
                            <DarwinSelect
                                displayName=""
                                execute={viewModel.setSupplierType}
                                fullWidth={true}
                                getOptionLabel={(option: any) => option.displayName}
                                options={viewModel.suppliers}
                                placeholder="Please select"
                                validationMessage={viewModel.getError("supplierId")}
                                value={viewModel.supplierType}
                            />
                        </div>
                    </ItemDetailsSectionRow>
                </ItemDetailsSection>
            )}

            {/* Age Section */}
            <ItemDetailsSection>
                <ItemDetailsSectionRow>
                    <div>
                        <span>Age:</span>
                        <DarwinInputWrapper type="number" viewModel={viewModel} fieldName="age" validateOnBlur={true} editMode={!viewModel.IsLoading} />
                    </div>
                    <div>
                        <span>Over 18:</span>
                        <RadioGroup row value={viewModel.model.confirmOver18.toString()} onChange={(e) => viewModel.setConfirmOver18(e.target.value === "true")}>
                            <FormControlLabel value="true" control={<CustomRadio />} label="Yes" />
                            <FormControlLabel value="false" control={<CustomRadio />} label="No" />
                        </RadioGroup>
                    </div>
                </ItemDetailsSectionRow>
            </ItemDetailsSection>

            {/* Adjustments Section */}
            <ItemDetailsSection>
                <p>
                    <span>Do you require any adjustments to carry out your role?</span>
                </p>
                <RadioGroup row value={viewModel.model.confirmAdjustments.toString()} onChange={(e) => viewModel.setConfirmAdjustments(e.target.value === "true")}>
                    <FormControlLabel value="true" control={<CustomRadio />} label="Yes" />
                    <FormControlLabel value="false" control={<CustomRadio />} label="No" />
                </RadioGroup>
                {viewModel.model.confirmAdjustments && (
                    <AnswerTileContainer>
                        <p>
                            <span>Details: </span>
                        </p>
                        <AnswerTile>
                            <DarwinInputWrapper multiLine rows={4} viewModel={viewModel} fieldName="adjustmentDetails" validateOnBlur={true} editMode={!viewModel.IsLoading} />
                        </AnswerTile>
                    </AnswerTileContainer>
                )}
            </ItemDetailsSection>

            {/* Health Problems Section */}
            <ItemDetailsSection>
                <p>
                    <span>Do you have any health problems that could affect your ability to carry out your tasks safely?</span>
                </p>
                <RadioGroup row value={viewModel.model.healthProblems.toString()} onChange={(e) => viewModel.setHealthProblems(e.target.value === "true")}>
                    <FormControlLabel value="true" control={<CustomRadio />} label="Yes" />
                    <FormControlLabel value="false" control={<CustomRadio />} label="No" />
                </RadioGroup>
                {viewModel.model.healthProblems && (
                    <AnswerTileContainer>
                        <p>
                            <span>Details: </span>
                        </p>
                        <AnswerTile>
                            <DarwinInputWrapper multiLine rows={4} viewModel={viewModel} fieldName="healthDetails" validateOnBlur={true} editMode={!viewModel.IsLoading} />
                        </AnswerTile>
                    </AnswerTileContainer>
                )}
            </ItemDetailsSection>

            {/* Skills/Qualifications Table */}
            <ItemDetailsSection>
                <p>
                    <span>Skills/Training/Qualifications</span>
                </p>
                <QualificationTable>
                    <div className="columns">
                        <div className="column">Name</div>
                        <div className="column">Yes/No</div>
                        <div className="column">Type/Level</div>
                        <div className="column">ID/Roll number</div>
                        <div className="column">Expiry date</div>
                        <div className="column">Evidence</div>
                    </div>
                    <div className="rows">
                        {viewModel.skillsQualifications.map((qualViewModel, index) => (
                            <div className="row" key={qualViewModel.model.id}>
                                <div className="cell">
                                    <DarwinInputWrapper viewModel={qualViewModel} fieldName="name" validateOnBlur={true} editMode={false} />
                                </div>
                                <div className="cell">
                                    <RadioGroup
                                        row
                                        value={qualViewModel.model.passed?.toString() || "na"}
                                        onChange={(e) => qualViewModel.setPassed(e.target.value === "true" ? true : e.target.value === "false" ? false : null)}
                                    >
                                        <FormControlLabel value="true" control={<CustomRadio />} label="Yes" />
                                        <FormControlLabel value="false" control={<CustomRadio />} label="No" />
                                        <FormControlLabel value="na" control={<CustomRadio />} label="N/A" />
                                    </RadioGroup>
                                </div>
                                {qualViewModel.model.passed === true && (
                                    <>
                                        <div className="cell">
                                            <DarwinInputWrapper viewModel={qualViewModel} fieldName="typeLevel" validateOnBlur={true} editMode={!viewModel.IsLoading} />
                                        </div>
                                        <div className="cell">
                                            <DarwinInputWrapper viewModel={qualViewModel} fieldName="idRollNumber" validateOnBlur={true} editMode={!viewModel.IsLoading} />
                                        </div>
                                        <div className="cell">
                                            <DarwinDateSelect
                                                displayName=""
                                                execute={qualViewModel.setExpiryDate}
                                                minDate={moment.utc().toDate()}
                                                format="DD/MM/YYYY"
                                                onBlur={() => qualViewModel.isFieldValid("expiryDate")}
                                                placeholder="Please select"
                                                validationMessage={qualViewModel.getError("expiryDate")}
                                                value={qualViewModel.model.expiryDate}
                                            />
                                        </div>
                                        <div className="cell">{/* Evidence handling */}</div>
                                    </>
                                )}
                                {(qualViewModel.model.passed === false || qualViewModel.model.passed === null) && (
                                    <>
                                        <div className="cell">-</div>
                                        <div className="cell">-</div>
                                        <div className="cell">-</div>
                                        <div className="cell">-</div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </QualificationTable>
            </ItemDetailsSection>

            {/* Plant and Equipment Qualifications Table */}
            <ItemDetailsSection>
                <p>
                    <span>Plant and Equipment Training/Qualifications</span>
                </p>
                <QualificationTable>
                    <div className="columns">
                        <div className="column">Name</div>
                        <div className="column">Yes/No</div>
                        <div className="column">Type/Level</div>
                        <div className="column">ID/Roll number</div>
                        <div className="column">Expiry date</div>
                        <div className="column">Evidence</div>
                    </div>
                    <div className="rows">
                        {viewModel.plantQualifications.map((qualViewModel, index) => (
                            <div className="row" key={qualViewModel.model.id}>
                                <div className="cell">
                                    <DarwinInputWrapper viewModel={qualViewModel} fieldName="name" validateOnBlur={true} editMode={false} />
                                </div>
                                <div className="cell">
                                    <RadioGroup
                                        row
                                        value={qualViewModel.model.passed?.toString() || "na"}
                                        onChange={(e) => qualViewModel.setPassed(e.target.value === "true" ? true : e.target.value === "false" ? false : null)}
                                    >
                                        <FormControlLabel value="true" control={<CustomRadio />} label="Yes" />
                                        <FormControlLabel value="false" control={<CustomRadio />} label="No" />
                                        <FormControlLabel value="na" control={<CustomRadio />} label="N/A" />
                                    </RadioGroup>
                                </div>
                                {qualViewModel.model.passed === true && (
                                    <>
                                        <div className="cell">
                                            <DarwinInputWrapper viewModel={qualViewModel} fieldName="typeLevel" validateOnBlur={true} editMode={!viewModel.IsLoading} />
                                        </div>
                                        <div className="cell">
                                            <DarwinInputWrapper viewModel={qualViewModel} fieldName="idRollNumber" validateOnBlur={true} editMode={!viewModel.IsLoading} />
                                        </div>
                                        <div className="cell">
                                            <DarwinDateSelect
                                                displayName=""
                                                execute={qualViewModel.setExpiryDate}
                                                minDate={moment.utc().toDate()}
                                                format="DD/MM/YYYY"
                                                onBlur={() => qualViewModel.isFieldValid("expiryDate")}
                                                placeholder="Please select"
                                                validationMessage={qualViewModel.getError("expiryDate")}
                                                value={qualViewModel.model.expiryDate}
                                            />
                                        </div>
                                        <div className="cell">{/* Evidence handling */}</div>
                                    </>
                                )}
                                {(qualViewModel.model.passed === false || qualViewModel.model.passed === null) && (
                                    <>
                                        <div className="cell">-</div>
                                        <div className="cell">-</div>
                                        <div className="cell">-</div>
                                        <div className="cell">-</div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </QualificationTable>
            </ItemDetailsSection>

            {/* PPE Section */}
            <ItemDetailsSection>
                <Step4Container>
                    <div className="root">
                        <Typography>What Personal Protective Equipment have you brought with you (please do not add skills training here)?</Typography>
                        <div className="form-controls">
                            {viewModel.personalProtectiveEquipment.map((ppe) => {
                                const ppeTypes = viewModel.getPersonalProtectiveEquipmentTypesByGroupId(ppe.id);

                                return (
                                    <FormControl className="form-control" component="fieldset" key={ppe.id}>
                                        <FormLabel className="form-label" component="legend">
                                            {ppe.displayName}
                                        </FormLabel>
                                        <FormHelperText className="form-helpertext">{ppe.statement}</FormHelperText>
                                        <FormGroup className="form-group">
                                            {ppeTypes.map((ppeType) => (
                                                <FormControlLabel
                                                    key={ppeType.id}
                                                    control={
                                                        <Checkbox
                                                            checked={viewModel.isPersonalProtectiveEquipmentTypeSelected(ppeType.id)}
                                                            name={ppeType.id}
                                                            onChange={() => viewModel.togglePersonalProtectiveEquipmentType(ppeType.id)}
                                                        />
                                                    }
                                                    label={ppeType.displayName}
                                                />
                                            ))}
                                        </FormGroup>
                                        <DashedDivider gutterBottom />
                                    </FormControl>
                                );
                            })}
                        </div>
                    </div>
                </Step4Container>
            </ItemDetailsSection>

            {/* Save Button */}
            <ItemDetailsSection>
                <PrimaryButton displayName="Save Changes" execute={viewModel.saveInduction} canExecute={!viewModel.IsLoading} fullWidth={false} />
            </ItemDetailsSection>
        </ItemDetailsContainer>
    );
});

export const InductionAdminEditView = styled(InductionAdminEditViewBase)``;
